import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Archive, Eye, GripVertical, Info, Plus, X } from "lucide-react";
import { useEffect, useState } from "react";

import { FilePreviewDialog, type PreviewFile } from "@/components/files/file-preview-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogPanel,
  DialogTitle,
} from "@/components/ui/dialog";

import type { ClaimWithRelations } from "../api/use-claim";
import { useGenerateMediationFormArchive } from "../api/use-claim-actions";

type ClaimDocument = ClaimWithRelations["claimDocuments"][number];

/** Pre-selected document types, in archive order — mirrors GenerateMediationFormArchive.vue REQUIRED_TYPES. */
const REQUIRED_TYPES = [
  "MEDIATION_ONE_PAGER",
  "EMAIL_CONVERSATION",
  "BOOKING_CERTIFICATE",
  "ETICKET",
  "DEBT_CONTRACT",
  "ID_PROOF",
  "DEBT_CONTRACT_TRAVEL_AGENCY_TO_UPCLAIM",
  "MED",
  "MED_LRE_EMAIL_SENT",
  "MED_RECEIPT",
  "MED_NEGLECTED",
  "PROOF_OF_EXPENSE",
  "PASSENGER_DECLARATION",
];

function documentLabel(claimDocument: ClaimDocument): string {
  return claimDocument.documentType.type === "OTHER"
    ? (claimDocument.file.name ?? claimDocument.documentType.displayName)
    : claimDocument.documentType.displayName;
}

function toPreviewFile(file: ClaimDocument["file"]): PreviewFile {
  return {
    id: file.id,
    name: file.name ?? null,
    mimeType: file.mimeType ?? null,
    size: file.size ?? null,
    boxFileId: file.boxFileId ?? null,
  };
}

function SortableFileRow({
  claimDocument,
  index,
  onRemove,
  onPreview,
}: {
  claimDocument: ClaimDocument;
  index: number;
  onRemove: () => void;
  onPreview: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: claimDocument.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between py-4 pr-5 text-sm leading-6"
    >
      <div className="ml-4 flex min-w-0 flex-1 items-center gap-2">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab touch-none text-gray-400 hover:text-gray-600"
          aria-label="Réordonner"
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <span className="inline-flex items-center rounded-full bg-pink-50 px-2 py-1 text-xs font-medium text-pink-700 ring-1 ring-pink-700/10 ring-inset">
          Pièce № {index + 1}
        </span>
        <button
          type="button"
          onClick={onPreview}
          className="cursor-pointer truncate text-left font-medium underline-offset-2 hover:underline"
        >
          {documentLabel(claimDocument)}
        </button>
      </div>
      <div className="ml-4 flex shrink-0 space-x-3">
        <button
          type="button"
          onClick={onPreview}
          className="cursor-pointer rounded-md font-medium text-blue-600 hover:text-blue-500"
          aria-label="Prévisualiser"
        >
          <Eye className="h-4 w-4" />
        </button>
        <span className="text-gray-200" aria-hidden="true">
          |
        </span>
        <button
          type="button"
          onClick={onRemove}
          className="cursor-pointer rounded-md font-medium text-gray-400 hover:text-red-500"
          aria-label="Retirer de la liste"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </li>
  );
}

interface MediationFormArchiveModalProps {
  claim: ClaimWithRelations;
  open: boolean;
  onClose: () => void;
}

/**
 * Port of modals/MediationFormArchiveModal.vue + overview/GenerateMediationFormArchive.vue.
 * Drag-reorder via dnd-kit (restores the Vue vuedraggable UX); click a document to preview it.
 */
export function MediationFormArchiveModal({ claim, open, onClose }: MediationFormArchiveModalProps) {
  const generateArchive = useGenerateMediationFormArchive(claim.id);

  const [filesIn, setFilesIn] = useState<ClaimDocument[]>([]);
  const [previewedFile, setPreviewedFile] = useState<PreviewFile | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  // Prefill with the required types, in REQUIRED_TYPES order — mirrors the Vue watcher.
  useEffect(() => {
    if (open) {
      setFilesIn(
        REQUIRED_TYPES.flatMap((type) =>
          claim.claimDocuments.filter((claimDocument) => claimDocument.documentType.type === type),
        ),
      );
    }
  }, [open, claim]);

  const availableDocuments = claim.claimDocuments.filter(
    (claimDocument) => !filesIn.some((file) => file.id === claimDocument.id),
  );

  function addDocument(claimDocument: ClaimDocument) {
    setFilesIn((files) => [...files, claimDocument]);
  }

  function removeFromOrder(index: number) {
    setFilesIn((files) => files.filter((_, i) => i !== index));
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    setFilesIn((files) => {
      const oldIndex = files.findIndex((file) => file.id === active.id);
      const newIndex = files.findIndex((file) => file.id === over.id);
      return arrayMove(files, oldIndex, newIndex);
    });
  }

  return (
    <>
      <FilePreviewDialog
        open={Boolean(previewedFile)}
        file={previewedFile}
        onClose={() => setPreviewedFile(null)}
      />

      <Dialog open={open} onOpenChange={(nextOpen) => (!nextOpen ? onClose() : undefined)}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Télécharger le dossier de médiation</DialogTitle>
            <DialogDescription>
              Sélectionner et réorganiser les documents à inclure dans l'archive.
            </DialogDescription>
          </DialogHeader>
          <DialogPanel>
            <h3 className="mb-2 text-sm leading-7 font-medium">Documents dans l'archive de médiation</h3>
            <ul className="mb-2 w-full divide-y divide-blue-50 rounded-md border border-dashed border-blue-200 bg-white shadow-sm">
              {!filesIn.length ? (
                <li className="flex items-center py-4 pr-5 pl-4 text-sm leading-6">
                  <Info className="ml-2 h-5 w-5 shrink-0 text-gray-400" aria-hidden="true" />
                  <span className="ml-4 flex items-center truncate font-medium">
                    Aucun document pour l'instant
                  </span>
                </li>
              ) : null}
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext
                  items={filesIn.map((file) => file.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {filesIn.map((claimDocument, index) => (
                    <SortableFileRow
                      key={claimDocument.id}
                      claimDocument={claimDocument}
                      index={index}
                      onRemove={() => removeFromOrder(index)}
                      onPreview={() => setPreviewedFile(toPreviewFile(claimDocument.file))}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </ul>

            {availableDocuments.length ? (
              <>
                <h3 className="mb-2 text-sm leading-7 font-medium">Documents du dossier</h3>
                <ul className="mb-5 max-h-64 w-full divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200 bg-gray-50">
                  {availableDocuments.map((claimDocument) => (
                    <li
                      key={claimDocument.id}
                      className="flex items-center justify-between py-2 pr-5 pl-4 text-sm leading-6"
                    >
                      <button
                        type="button"
                        className="min-w-0 flex-1 cursor-pointer truncate text-left text-gray-600"
                        onClick={() => setPreviewedFile(toPreviewFile(claimDocument.file))}
                      >
                        {documentLabel(claimDocument)}
                      </button>
                      <button
                        type="button"
                        onClick={() => addDocument(claimDocument)}
                        className="ml-4 cursor-pointer rounded-md font-medium text-blue-600 hover:text-blue-500"
                        aria-label="Ajouter à l'archive"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}

            <Button
              onClick={() => generateArchive.mutate(filesIn.map(({ id }) => id))}
              disabled={generateArchive.isPending || !filesIn.length}
            >
              <Archive />
              Générer et télécharger l'archive
            </Button>
          </DialogPanel>
        </DialogContent>
      </Dialog>
    </>
  );
}
